import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import type { FixingIndex } from '../types/FixingIndex';
import type { DateOnly } from './dateOnly';
import { logger } from './logger';

const FIXING_TIME_WITH_SECONDS_FORMAT = 'yyyy-MM-dd HH:mm:ss z';

function getNominalIndexTimeString(validNominalIndexTime: number | undefined): string | undefined {
  if (!Number.isFinite(validNominalIndexTime)) {
    return undefined;
  }

  const paddedTime = `${Math.trunc(validNominalIndexTime ?? 0)}`.padStart(6, '0');
  if (paddedTime.length !== 6) {
    return undefined;
  }

  const hours = Number(paddedTime.slice(0, 2));
  const minutes = Number(paddedTime.slice(2, 4));
  const seconds = Number(paddedTime.slice(4, 6));
  if (hours > 23 || minutes > 59 || seconds > 59) {
    return undefined;
  }

  return `${paddedTime.slice(0, 2)}:${paddedTime.slice(2, 4)}:${paddedTime.slice(4, 6)}`;
}

/**
 * Given a FixingIndex and a DateOnly, combines:
 * - FixingIndex.ValidNominalIndexTime (time)
 * - provided date (date)
 * and renders in the index timezone as: `yyyy-MM-dd HH:mm:ss z`.
 */
export function formatFixingTimeAtDateOnly(fixingIndex: FixingIndex, dateOnly: DateOnly | undefined): string {
  try {
    if (!fixingIndex.NominalIndexTimeZone || !dateOnly) {
      return '';
    }

    const nominalIndexTime = getNominalIndexTimeString(fixingIndex.ValidNominalIndexTime);
    if (!nominalIndexTime) {
      return '';
    }

    const fixingDateTimeInUtc = zonedTimeToUtc(`${dateOnly} ${nominalIndexTime}`, fixingIndex.NominalIndexTimeZone);
    return formatInTimeZone(fixingDateTimeInUtc, fixingIndex.NominalIndexTimeZone, FIXING_TIME_WITH_SECONDS_FORMAT);
  } catch (e) {
    logger.error(e as Error);
    return '';
  }
}
